import { useEffect, useRef } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import {
  AUTO_RUN_STAGE_PAUSE_MS,
  markStageForPlayback,
  useAutoRun,
  type StageKey,
} from "@/lib/playback";
import type { PlaybackStatus } from "@/hooks/usePlayback";

type NextStage = Exclude<StageKey, "intake"> | null;

type Options = {
  appId: string;
  stage: StageKey;
  // Whether this stage matches app.stage — only the "current" stage should
  // drive the pipeline forward.
  isCurrent: boolean;
  // Either "complete" (playback animation finished, or not applicable) or
  // "playing"/"idle" (wait).
  playbackStatus: PlaybackStatus;
  // The existing React Query mutation that calls api.advance(id). The hook
  // triggers .mutate() when it's time to advance.
  advanceMutation: UseMutationResult<unknown, unknown, void, unknown>;
  // The stage we expect to be on after advance. Used to mark playback so the
  // next page animates its events. null ⇒ nothing to advance to.
  nextStage: NextStage;
  // Called after the pause elapses, just before advanceMutation.mutate(). Lets
  // the caller navigate the router.
  onAdvance?: () => void;
};

// useAutoAdvance wires a stage page into the "Run full journey" mode. When
// the app is in auto-run, this stage is the current stage, and its playback
// (if any) has completed, the hook schedules api.advance() after a short
// human-readable pause so the audience can take in the outcome.
export function useAutoAdvance({
  appId,
  stage,
  isCurrent,
  playbackStatus,
  advanceMutation,
  nextStage,
  onAdvance,
}: Options): void {
  const { isAutoRunning } = useAutoRun();
  const running = isAutoRunning(appId);
  const firedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the "already fired" guard whenever we navigate to a different stage
  // — otherwise re-entering the page for a second auto-run would be a no-op.
  useEffect(() => {
    firedRef.current = false;
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [stage, appId]);

  useEffect(() => {
    if (!running) return;
    if (!isCurrent) return;
    if (playbackStatus !== "complete") return;
    if (!nextStage) return;
    if (firedRef.current) return;
    if (advanceMutation.isPending) return;

    firedRef.current = true;
    timerRef.current = setTimeout(() => {
      markStageForPlayback(appId, nextStage);
      onAdvance?.();
      advanceMutation.mutate();
    }, AUTO_RUN_STAGE_PAUSE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, running, isCurrent, playbackStatus, nextStage]);
}
